import { useTranslation } from "react-i18next";
import {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
  useContext,
} from "react";
import clsx from "classnames";
import style from "./Input.module.scss";
import VerticalFlex from "layouts/flex/VerticalFlex";
import FlexChild from "layouts/flex/FlexChild";
import VerticalMiddleWrapper from "layouts/wrapper/VerticalMiddleWrapper";
import HorizontalFlex from "layouts/flex/HorizontalFlex";
import useAltEffect from "shared/hooks/useAltEffect";
import ArrowBox from "./modules/ArrowBox";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { faMinus } from "@fortawesome/free-solid-svg-icons";
import Center from "layouts/wrapper/Center";
import { BrowserDetectContext } from "providers/BrowserEventProvider";
import Inline from "layouts/container/Inline";

const InputNumber = forwardRef((props, ref) => {
  const input = useRef();
  const {
    min,
    max,
    placeHolder,
    label,
    labelWidth,
    clearButton,
    disabled,
    required,
    allowedQuantity,
    hideLeftArrow,
    hideRightArrow,
    hideArrow,
    borderless,
    isAllowed = true,
    prefix,
    suffix,
    onKeyDown,
  } = props;
  const { t } = useTranslation();
  const [name, setName] = useState(props.name || "");
  const [value, setValue] = useState(props.value ? props.value : 0);
  const [isValid, setValid] = useState(required ? false : true);
  const [helperText, setHelperText] = useState("");
  const [isEmpty, setEmpty] = useState(true);
  const [minBlock, setMinBlock] = useState(false);
  const [maxBlock, setMaxBlock] = useState(false);
  const { isMobile } = useContext(BrowserDetectContext);

  useEffect(() => {
    const handleWheel = (event) => {
      event.stopPropagation();
      event.preventDefault();
    };

    // 모든 input 태그에 대해 이벤트 리스너 추가
    const inputElements = document.querySelectorAll("input");
    inputElements.forEach((input) => {
      input.addEventListener("wheel", handleWheel);
    });

    // 컴포넌트 언마운트 시 이벤트 리스너 제거
    return () => {
      inputElements.forEach((input) => {
        input.removeEventListener("wheel", handleWheel);
      });
    };
  }, []);

  // const changeValue = (v) => {
  //     let changedValue = Math.max(min ? min : 0, Math.min(max ? max : 99999, v));
  //     if (props.onChange) {
  //         props.onChange(changedValue);
  //     }
  //     setValue(changedValue);
  // }

  const changeValue = (v) => {
    let changedValue = Math.max(
      min ? min : 0,
      Math.min(max || (typeof max === "number" && max === 0) ? max : 99999, v)
    );

    if (allowedQuantity !== undefined && changedValue > allowedQuantity) {
      changedValue = allowedQuantity;
    }

    if (props.onChange) {
      props.onChange(changedValue);
    }
    setValue(changedValue);
  };

  const onChange = (e) => {
    let inputValue = Number(e.target.value);
    if (isEmpty) {
      setEmpty(false);
    }
    changeValue(inputValue);
  };

  const onClearButtonClick = () => {
    setValue(0);
  };
  const onPlusClick = (e) => {
    if (isAllowed) {
      e.stopPropagation();
      if (disabled) return;
      if (isEmpty) {
        setEmpty(false);
      }
      changeValue(value + 1);
    } else {
      alert("더 이상 고를 수 없습니다.");
    }
  };
  const onMinusClick = (e) => {
    e.stopPropagation();
    if (disabled) return;

    if (isEmpty) {
      setEmpty(false);
    }
    changeValue(value - 1);
  };

  useImperativeHandle(ref, () => ({
    getName() {
      return name;
    },
    getValue() {
      return value;
    },
    setValue(value) {
      setValue(value);
    },
    isValid() {
      return isValid;
    },
    focus() {
      if (isValid) {
        setHelperText("");
      } else {
        setHelperText("수량을 입력해주세요");
      }
      input.current.scrollTo(0, input.current.offsetTop);
    },
  }));

  useAltEffect(() => {
    if (props.value) {
      setValue(props.value);
    }
  }, [props.value]);

  useAltEffect(() => {
    if (required) {
      if (value > 0) {
        setValid(true);
      } else {
        setValid(false);
      }
    }

    if (value <= min) setMinBlock(true);
    else setMinBlock(false);

    if (value >= max) setMaxBlock(true);
    else setMaxBlock(false);
  }, [value]);

  useEffect(() => {
    if (isValid) {
      setHelperText("");
    } else {
      if (!isEmpty) {
        setHelperText("수량을 입력해주세요");
      }
    }
  }, [isValid, isEmpty]);
  return (
    <VerticalFlex>
      <FlexChild height={props.height}>
        <HorizontalFlex>
          {label ? (
            <FlexChild width={labelWidth || "max-content"}>
              <p>{label}</p>
            </FlexChild>
          ) : null}
          {!hideArrow && !hideLeftArrow && (
            <FlexChild width={"initial"}>
              <span
                onClick={onMinusClick}
                className={clsx(
                  isMobile ? style.mobileLeftArrow : style.leftArrow,
                  { [style.active]: !disabled && !minBlock }
                )}
              >
                <Center>
                  <FontAwesomeIcon icon={faMinus} />
                </Center>
              </span>
            </FlexChild>
          )}
          <FlexChild>
            <div ref={input} className={style.wrap}>
              <HorizontalFlex alignItems="center">
                {prefix && (
                  <p
                    className={clsx(
                      isMobile ? style.mobileInput : style.input,
                      {
                        [style.borderless]: hideArrow || hideLeftArrow,
                      }
                    )}
                  >
                    {prefix}
                  </p>
                )}
                <input
                  type="number"
                  name={name}
                  className={clsx(
                    isMobile ? style.mobileInput : style.input,
                    { [style.borderless]: borderless || true },
                    {
                      [style.withPlaceHolder]: placeHolder ? true : false,
                      [style.hideLeftArrow]: hideArrow || hideLeftArrow,
                      [style.hideRightArrow]: hideArrow || hideRightArrow,
                    }
                  )}
                  style={props.style}
                  onChange={onChange}
                  onKeyDown={(e) => onKeyDown?.(e, value)}
                  value={Number(value || 0).toString()}
                  disabled={disabled ? true : null}
                />
                {suffix && (
                  <p
                    className={clsx(
                      isMobile ? style.mobileInput : style.input,
                      {
                        [style.borderless]: hideArrow || hideRightArrow,
                      }
                    )}
                  >
                    {suffix}
                  </p>
                )}
              </HorizontalFlex>
              {placeHolder ? (
                <div className={style.placeHolderArea}>
                  <VerticalMiddleWrapper>
                    <span
                      className={clsx(style.placeHolder, {
                        [style.moveUp]: value && value.length > 0,
                      })}
                    >
                      {props.placeHolder}
                    </span>
                  </VerticalMiddleWrapper>
                </div>
              ) : null}
              {clearButton ? (
                <div className={style.buttonArea}>
                  <span
                    className={style.clearButton}
                    onClick={onClearButtonClick}
                  >
                    &times;{" "}
                  </span>
                </div>
              ) : null}
            </div>
          </FlexChild>
          {!hideArrow && !hideRightArrow && (
            <FlexChild width={"initial"}>
              <span
                onClick={onPlusClick}
                className={clsx(
                  isMobile ? style.mobileRightArrow : style.rightArrow,
                  { [style.active]: !disabled && !maxBlock }
                )}
              >
                <Center>
                  <FontAwesomeIcon icon={faPlus} />
                </Center>
              </span>
            </FlexChild>
          )}
        </HorizontalFlex>
        {/* <ArrowBox visible={helperText.length > 0}><p>{helperText}</p></ArrowBox> */}
      </FlexChild>
    </VerticalFlex>
  );
});

export default InputNumber;
